import { motion } from "framer-motion";
import { Phone, MessageCircle, MapPin, Clock, Star, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppointment } from "@/context/AppointmentContext";
import { SITE, whatsappHref, IMAGES } from "@/data/site";

export function Hero() {
  const { openDialog } = useAppointment();

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.heroBg} alt="Advanced diagnostic imaging suite at UNIRAD Diagnostics Lucknow" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/85 to-brand-navy/55" />
        <div className="absolute inset-0 grain opacity-40" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-xs font-semibold tracking-wide"
          >
            <ShieldCheck className="h-4 w-4 text-brand-sky" /> Trusted Diagnostic Center in Lucknow
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-6 font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance"
          >
            Accurate Diagnostics.
            <span className="block font-semibold bg-gradient-to-r from-brand-sky to-white bg-clip-text text-transparent">
              Trusted Care.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="mt-5 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed"
          >
            Advanced Ultrasound, Digital X-Ray, Pathology, Interventions & Diagnostic Services in Lucknow — delivered with precision, speed and genuine patient care.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Button
              data-testid="hero-book-btn"
              onClick={() => openDialog()}
              className="h-13 rounded-full bg-booking hover:bg-booking-dark px-7 text-base shadow-lg shadow-booking/30 gap-2 py-6"
            >
              <Phone className="h-5 w-5" /> Book Appointment
            </Button>
            <a href={whatsappHref()} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn">
              <Button className="h-13 rounded-full px-7 text-base gap-2 py-6 bg-[#25D366] hover:bg-[#1faa55] text-white">
                <MessageCircle className="h-5 w-5" /> WhatsApp Us
              </Button>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-8 flex items-center gap-3 text-sm text-white/75"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span>Rated 4.9/5 by patients across Lucknow</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="lg:col-span-5"
        >
          <div className="rounded-2xl bg-white/95 backdrop-blur-xl border border-white/40 shadow-2xl p-6 sm:p-7 space-y-5">
            <h3 className="font-heading font-semibold text-brand-navy text-lg">Visit Us / Quick Info</h3>
            <InfoRow icon={Phone} title="Call Us">
              {SITE.phones.map((p) => p.number).join(" · ")}
            </InfoRow>
            <InfoRow icon={MapPin} title="Our Location">
              {SITE.address.line1}, {SITE.address.line2}, {SITE.address.city}
            </InfoRow>
            <InfoRow icon={Clock} title="Working Hours">
              Mon–Sat: 7:00 AM – 9:00 PM · Sun: 8:00 AM – 2:00 PM
            </InfoRow>
            <a href={SITE.mapLink} target="_blank" rel="noopener noreferrer" className="block">
              <div className="rounded-xl overflow-hidden border border-border h-32">
                <iframe
                  title="UNIRAD Diagnostics location"
                  src={SITE.mapEmbed}
                  className="w-full h-full pointer-events-none"
                  loading="lazy"
                />
              </div>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, title, children }) {
  return (
    <div className="flex gap-3">
      <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand-soft text-brand shrink-0">
        <Icon className="h-5 w-5" strokeWidth={1.8} />
      </span>
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-brand">{title}</p>
        <p className="text-sm text-brand-navy/80 leading-snug">{children}</p>
      </div>
    </div>
  );
}
